import { CLASS_IDS, classColor, GLYPHS, PALETTE } from "@eldrun/content";
import { BIOME, PLAN_SIDE, PLAN_SPACING, WORLD_HALF } from "@eldrun/worldgen";
import { PerspectiveCamera } from "three";
import { NavHud } from "../game/scene/nav-hud.ts";
import { gameColor } from "../render/style/roles.ts";
import { ChatBox } from "../ui/hud/chat.ts";
import { ClockHud } from "../ui/hud/clock-hud.ts";
import { GameHud } from "../ui/hud/hud.ts";
import { SkillBar } from "../ui/hud/skill-bar.ts";
import { skillBarView } from "../ui/hud/skill-bar-model.ts";
import { toast } from "../ui/hud/toast.ts";
import { TutorialCard } from "../ui/hud/tutorial.ts";
import { Tutorial, tutorialKeys } from "../ui/hud/tutorial-model.ts";
import { h } from "../ui/kit/dom.ts";
import { IslandLayers } from "../ui/panels/island-map.ts";
import { FogOfWar, type MapMarker } from "../ui/panels/map-model.ts";

/**
 * Plano de exemplo para o minimapa da vitrine (sem servidor nem gerador): uma costa em diagonal perto da
 * origem, com o mar a sudoeste, como no mockup ui-b.
 */
function samplePlan(): { heights: Float32Array; biome: Uint8Array } {
  const heights = new Float32Array(PLAN_SIDE * PLAN_SIDE);
  const biome = new Uint8Array(PLAN_SIDE * PLAN_SIDE);
  for (let iz = 0; iz < PLAN_SIDE; iz++) {
    for (let ix = 0; ix < PLAN_SIDE; ix++) {
      const x = ix * PLAN_SPACING - WORLD_HALF;
      const z = iz * PLAN_SPACING - WORLD_HALF;
      const d = x - z * 0.8 + 70 + 22 * Math.sin(z / 30) + 10 * Math.cos(x / 17);
      const i = iz * PLAN_SIDE + ix;
      heights[i] = d < 0 ? -2 - Math.min(20, -d / 6) : d * 0.12 + 4 * Math.sin(x / 23) * Math.cos(z / 19);
      biome[i] = d < 0 ? BIOME.mar : d < 18 ? BIOME.praia : d < 110 ? BIOME.mata : BIOME.serra;
    }
  }
  return { heights, biome };
}

/**
 * HUD de exemplo na vitrine da UI (F81, `?debug=ui&painel=hud`): os objetivos (o tutorial no passo 3), a
 * bússola girada, o minimapa com aliados, ping, chefe e lugares, os botões do canto, um aviso, o
 * "interagir", o relógio, a barra de habilidades e o chat. `left` e `right` recebem também os quadros do
 * grupo e o Relicário da vitrine.
 */
export function startHudSample(slot: number): { left: HTMLElement; right: HTMLElement } {
  const left = h("div", { id: "hud-esquerda" });
  document.body.appendChild(left);
  const tutorial = new Tutorial({ v: 1, enabled: true, done: ["coletar", "cinto"], epoch: 0 });
  new TutorialCard(left, () => {}, () => {}).update(tutorial.view(0, tutorialKeys()));
  const nav = new NavHud(document.body, new IslandLayers(samplePlan()), [
    { id: "botao-opcoes", icon: "hud-icone-opcoes", label: "Opções", key: { action: "settings", name: "P" }, onClick: () => {} },
    { id: "botao-mochila", icon: "hud-icone-mochila", label: "Inventário", key: { action: "inventory", name: "Tab" }, onClick: () => {} },
    { id: "botao-mapa", icon: "hud-icone-mapa", label: "Mapa", key: { action: "map", name: "M" }, onClick: () => {} },
    { id: "botao-pausa", icon: "hud-icone-menu", label: "Pausa", key: { fixed: "Esc" }, onClick: () => {} },
  ]);
  // Você na origem olhando um pouco a leste do norte; dois aliados, um ping, o chefe e lugares descobertos.
  const fog = new FogOfWar();
  fog.reveal(0, 0, 140);
  fog.reveal(70, -90, 80);
  // A cor da classe de índice `k` (F85: a vitrine põe um jogador por classe).
  const color = (k: number) => gameColor(classColor(CLASS_IDS[k] ?? ""));
  const me = Math.max(0, slot);
  const allies = [1, 2, 3].filter((k) => k !== me).slice(0, 2);
  const markers: MapMarker[] = [
    { id: "amostra-1", kind: "carranca", x: -40, z: 60, label: "Carranca", symbol: "♜", color: PALETTE.neutro.hex },
    { id: "amostra-2", kind: "pedra", x: 62, z: 38, label: "Pedra de Encanto", symbol: "◈", color: PALETTE.neutro.hex },
    { id: "amostra-3", kind: "ping", x: 30, z: -70, label: "Bia: Aqui", symbol: "◆", color: color(allies[0]!) },
    { id: "amostra-4", kind: "chefe", x: -160, z: -150, label: "Ciclope", symbol: GLYPHS.coroa.char, color: gameColor(PALETTE.chefe.hex) },
    { id: "amostra-5", kind: "aliado", x: 55, z: -45, label: "Bia", symbol: String(allies[0]! + 1), color: color(allies[0]!) },
    { id: "amostra-6", kind: "aliado", x: -60, z: 80, label: "Caio", symbol: String(allies[1]! + 1), color: color(allies[1]!) },
    { id: "amostra-7", kind: "eu", x: 0, z: 0, label: "Você", symbol: "▲", color: PALETTE.neutro.hex, yaw: -0.35 },
  ];
  const camera = new PerspectiveCamera();
  camera.position.set(0, 2, 0);
  camera.rotation.set(0, -0.35, 0, "YXZ");
  camera.updateMatrixWorld();
  // O canvas do minimapa só tem tamanho depois do layout.
  requestAnimationFrame(() => {
    nav.setMarkers(0, 0, fog, markers, []);
    nav.frame(camera);
  });
  toast("Ciclope vem aí: junte o grupo antes do pôr do sol.", "info", 600_000);
  new GameHud(document.body).setTarget("Bancada · E abre");
  new ClockHud(document.body).update({ day: 2, night: false, elapsed: 120, remaining: 300, progress: 0.3, phase: 0.15, dusk: false }, 0, "");
  new SkillBar(document.body).update(skillBarView("baluarte", 4000, 0, { ativa: "botão direito", ultimate: "V" }, 60));
  const chat = new ChatBox(document.body);
  chat.push({ kind: "sistema", text: "Bia entrou na partida.", at: performance.now() });
  chat.push({ kind: "fala", text: "bora pra Bancada", name: "Bia", from: "b", at: performance.now() });
  chat.update(performance.now());
  return { left, right: nav.column };
}
